import { useEffect, useId, useState } from "react";
import { Trash2 } from "lucide-react";
import { useAppStore } from "@/store/useAppStore";
import { useLiveRecords } from "@/hooks/useDomain";
import type { EventRecord } from "@/lib/entities";
import { newCalendar, newEvent, touch } from "@/lib/records";
import { parseRRule, describeRRule } from "@/lib/recurrence";
import { endOfDay } from "@/lib/date";
import {
  combineDateAndTime,
  nextRoundTime,
  toDateInput,
  toTimeInput,
} from "@/lib/datetime";
import { Dialog } from "@/ui/Dialog";
import { Button } from "@/ui/Button";
import { Field, Input, Select, LabelledInput, Textarea } from "@/ui/Field";
import { Switch } from "@/ui/Switch";
import { toast } from "@/ui/Toast";

type Repeat = "NONE" | "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";

const REPEAT_LABELS: Record<Repeat, string> = {
  NONE: "Không lặp",
  DAILY: "Hằng ngày",
  WEEKLY: "Hằng tuần",
  MONTHLY: "Hằng tháng",
  YEARLY: "Hằng năm",
};

const ALERTS = [
  { value: 0, label: "Không nhắc" },
  { value: 5, label: "5 phút trước" },
  { value: 10, label: "10 phút trước" },
  { value: 15, label: "15 phút trước" },
  { value: 30, label: "30 phút trước" },
  { value: 60, label: "1 giờ trước" },
  { value: 1440, label: "1 ngày trước" },
];

export interface EventDialogProps {
  open: boolean;
  onClose: () => void;
  /** Existing event to edit, or `null` to create a new one. */
  event?: EventRecord | null;
  /** Start timestamp used when creating from a specific day. */
  defaultStart?: number;
}

export function EventDialog({
  open,
  onClose,
  event,
  defaultStart,
}: EventDialogProps) {
  const formId = useId();
  const upsert = useAppStore((s) => s.upsert);
  const remove = useAppStore((s) => s.remove);
  const calendars = useLiveRecords<{ id: string; title: string }>("calendars");

  const [title, setTitle] = useState("");
  const [calendarId, setCalendarId] = useState("");
  const [allDay, setAllDay] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("09:00");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("10:00");
  const [location, setLocation] = useState("");
  const [url, setUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [repeat, setRepeat] = useState<Repeat>("NONE");
  const [repeatUntil, setRepeatUntil] = useState("");
  const [alert, setAlert] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Reset the form when the dialog opens. Deliberately keyed on `open` (and
  // the id) so background syncs cannot wipe what the user is typing.
  useEffect(() => {
    if (!open) return;
    setError(null);

    if (event) {
      setTitle(event.title ?? "");
      setCalendarId(event.calendarId ?? "");
      setAllDay(Boolean(event.isAllDay));
      setStartDate(toDateInput(event.startEpochMillis));
      setStartTime(toTimeInput(event.startEpochMillis));
      setEndDate(toDateInput(event.endEpochMillis));
      setEndTime(toTimeInput(event.endEpochMillis));
      setLocation(event.location ?? "");
      setUrl(event.url ?? "");
      setNotes(event.notes ?? "");
      setRepeat(parseRRule(event.rrule)?.freq ?? "NONE");
      setRepeatUntil(
        event.repeatEndEpochMillis ? toDateInput(event.repeatEndEpochMillis) : "",
      );
      setAlert(event.alertMinutesBefore ?? 0);
    } else {
      const start = nextRoundTime(defaultStart ?? Date.now());
      const end = start + 3_600_000;
      setTitle("");
      setCalendarId(calendars[0]?.id ?? "");
      setAllDay(false);
      setStartDate(toDateInput(start));
      setStartTime(toTimeInput(start));
      setEndDate(toDateInput(end));
      setEndTime(toTimeInput(end));
      setLocation("");
      setUrl("");
      setNotes("");
      setRepeat("NONE");
      setRepeatUntil("");
      setAlert(10);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, event?.id]);

  async function handleSubmit(submitEvent: React.FormEvent) {
    submitEvent.preventDefault();
    setError(null);

    const cleanTitle = title.trim();
    if (!cleanTitle) {
      setError("Đặt tiêu đề cho sự kiện trước đã.");
      return;
    }

    const startMs = allDay
      ? (combineDateAndTime(startDate, "00:00") ?? 0)
      : combineDateAndTime(startDate, startTime);
    const endMs = allDay
      ? endOfDay(combineDateAndTime(endDate || startDate, "00:00") ?? Date.now()).getTime()
      : combineDateAndTime(endDate, endTime);

    if (startMs === null || endMs === null) {
      setError("Ngày giờ không hợp lệ.");
      return;
    }
    if (endMs <= startMs) {
      setError("Giờ kết thúc phải sau giờ bắt đầu.");
      return;
    }

    setBusy(true);
    try {
      // A brand-new account has no calendar yet: create one on the fly
      // rather than blocking the user with a dead end.
      let targetCalendar = calendarId;
      if (!targetCalendar) {
        const created = newCalendar("Cá nhân");
        await upsert("calendars", created);
        targetCalendar = created.id;
      }

      const patch = {
        calendarId: targetCalendar,
        title: cleanTitle,
        startEpochMillis: startMs,
        endEpochMillis: endMs,
        isAllDay: allDay,
        rrule: repeat === "NONE" ? null : `FREQ=${repeat}`,
        repeatEndEpochMillis:
          repeat !== "NONE" && repeatUntil
            ? endOfDay(combineDateAndTime(repeatUntil, "00:00") ?? Date.now()).getTime()
            : null,
        alertMinutesBefore: alert || null,
        location: location.trim() || null,
        url: url.trim() || null,
        notes: notes.trim() || null,
      };

      if (event) {
        const record = useAppStore
          .getState()
          .data.events.find((item) => item.id === event.id);
        if (record) await upsert("events", touch({ ...record, ...patch }));
        toast({ title: "Đã lưu sự kiện", variant: "success" });
      } else {
        await upsert("events", newEvent({ ...patch, calendarId: targetCalendar }));
        toast({ title: "Đã thêm sự kiện", variant: "success" });
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Không lưu được sự kiện.");
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!event) return;
    const record = event;

    await remove("events", event.id);
    onClose();
    toast({
      title: "Đã xoá sự kiện",
      description: record.title,
      action: {
        label: "Hoàn tác",
        onClick: () => {
          void upsert("events", touch({ ...record, deletedAt: null }));
        },
      },
    });
  }

  const recurring = event?.rrule ? describeRRule(event.rrule) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={event ? "Sửa sự kiện" : "Sự kiện mới"}
      description={
        event && recurring
          ? `Sự kiện lặp ${recurring.toLowerCase()} — thay đổi áp dụng cho cả chuỗi.`
          : undefined
      }
      size="lg"
      footer={
        <>
          {event && (
            <Button
              variant="ghost"
              onClick={() => void handleDelete()}
              className="mr-auto text-danger hover:bg-dangerSoft hover:text-danger"
            >
              <Trash2 size={14} />
              Xoá
            </Button>
          )}
          <Button onClick={onClose}>Huỷ</Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={busy}
          >
            {event ? "Lưu thay đổi" : "Thêm sự kiện"}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} className="space-y-4">
        <Field label="Tiêu đề" htmlFor={`${formId}-title`}>
          <Input
            id={`${formId}-title`}
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ví dụ: Họp với khách hàng"
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Lịch" htmlFor={`${formId}-cal`}>
            <Select
              id={`${formId}-cal`}
              value={calendarId}
              onChange={(e) => setCalendarId(e.target.value)}
            >
              {calendars.length === 0 && (
                <option value="">Cá nhân (tạo mới)</option>
              )}
              {calendars.map((calendar) => (
                <option key={calendar.id} value={calendar.id}>
                  {calendar.title}
                </option>
              ))}
            </Select>
          </Field>

          <div className="flex items-end pb-2">
            <label className="flex cursor-pointer items-center gap-2.5 text-[13px] text-ink-soft">
              <Switch
                checked={allDay}
                onChange={setAllDay}
                label="Sự kiện cả ngày"
              />
              Cả ngày
            </label>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Bắt đầu">
            <div className="flex gap-2">
              <Input
                type="date"
                value={startDate}
                className="min-w-0 flex-1"
                onChange={(e) => {
                  setStartDate(e.target.value);
                  if (endDate < e.target.value) setEndDate(e.target.value);
                }}
              />
              <div className="w-[104px] shrink-0">
                <Input
                  type="time"
                  value={startTime}
                  disabled={allDay}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              </div>
            </div>
          </Field>

          <Field label="Kết thúc">
            <div className="flex gap-2">
              <Input
                type="date"
                value={endDate}
                min={startDate}
                className="min-w-0 flex-1"
                onChange={(e) => setEndDate(e.target.value)}
              />
              <div className="w-[104px] shrink-0">
                <Input
                  type="time"
                  value={endTime}
                  disabled={allDay}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              </div>
            </div>
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Lặp lại" htmlFor={`${formId}-repeat`}>
            <Select
              id={`${formId}-repeat`}
              value={repeat}
              onChange={(e) => setRepeat(e.target.value as Repeat)}
            >
              {(Object.keys(REPEAT_LABELS) as Repeat[]).map((value) => (
                <option key={value} value={value}>
                  {REPEAT_LABELS[value]}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Nhắc trước" htmlFor={`${formId}-alert`}>
            <Select
              id={`${formId}-alert`}
              value={alert}
              onChange={(e) => setAlert(Number(e.target.value))}
            >
              {ALERTS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {repeat !== "NONE" && (
          <LabelledInput
            label="Lặp đến ngày"
            hint="Để trống nếu không có ngày kết thúc."
            type="date"
            value={repeatUntil}
            min={startDate}
            onChange={(e) => setRepeatUntil(e.target.value)}
          />
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <LabelledInput
            label="Địa điểm"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Phòng họp, địa chỉ…"
          />
          <LabelledInput
            label="Liên kết"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://…"
          />
        </div>

        <Field label="Ghi chú" htmlFor={`${formId}-notes`}>
          <Textarea
            id={`${formId}-notes`}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Thêm mô tả, tài liệu tham khảo…"
            rows={3}
          />
        </Field>

        {error && (
          <p className="rounded-lg bg-dangerSoft px-3 py-2 text-[12px] text-danger">
            {error}
          </p>
        )}
      </form>
    </Dialog>
  );
}
